import { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Building2,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  GitBranch,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import Dialog from "@/components/atoms/Dialog";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { InfoHint } from "@/components/atoms/InfoHint";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { TimeWindowSlider } from "@/modules/availability/preview/TimeWindowSlider";
import { useSettingsStore } from "@/store/settingsStore";
import {
  flattenEntities,
  findEntityById,
} from "@/modules/settings/entityHelpers";
import type { LegalEntityHierarchy } from "@/services/profileService";
import {
  liveHoursService,
  type LiveHoursDay,
  type LiveHoursPayload,
  type LiveHoursTiming,
} from "@/services/liveHoursService";
import { DEFAULT_SLOT_SETTINGS, TIMEZONE_OPTIONS } from "@/types/availability";
import { toast } from "react-toastify";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { IconChip, SaveStatus } from "@/modules/settings/SettingsPrimitives";
import {
  SCOPE_BAR_CLASS,
  actionBarClass,
} from "@/modules/settings/settingsStyles";

type TimeMode = "SAME_ALL_DAYS" | "CUSTOM_PER_DAY";
type RepeatMode = "WEEKLY" | "DAILY";
type DayCode = "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT" | "SUN";
type TimeRange = { start: string; end: string };
type Timing = {
  /** Live round the clock: every day active, midnight to midnight, editor locked. */
  alwaysOn: boolean;
  timeMode: TimeMode;
  repeat: RepeatMode;
  /** IANA zone the windows below are interpreted in; applies to both time modes. */
  timezone: string;
  days: DayCode[];
  /** Windows applied to every active day in same-time mode. */
  sameTimes: TimeRange[];
  /** Custom mode holds several windows per day; same-time mode ignores it. */
  perDay: Record<DayCode, TimeRange[]>;
};

type SavedTiming = Timing & { legalEntityId: string; branchId: string | null };
/** Which window the inline editor under the day grid is pointed at. */
type EditTarget = { day: DayCode; index: number };

const DAYS: { code: DayCode; label: string }[] = [
  { code: "MON", label: "Mon" },
  { code: "TUE", label: "Tue" },
  { code: "WED", label: "Wed" },
  { code: "THU", label: "Thu" },
  { code: "FRI", label: "Fri" },
  { code: "SAT", label: "Sat" },
  { code: "SUN", label: "Sun" },
];
const API_DAYS: Record<DayCode, LiveHoursDay> = {
  MON: "MONDAY",
  TUE: "TUESDAY",
  WED: "WEDNESDAY",
  THU: "THURSDAY",
  FRI: "FRIDAY",
  SAT: "SATURDAY",
  SUN: "SUNDAY",
};
const UI_DAYS: Record<LiveHoursDay, DayCode> = {
  MONDAY: "MON",
  TUESDAY: "TUE",
  WEDNESDAY: "WED",
  THURSDAY: "THU",
  FRIDAY: "FRI",
  SATURDAY: "SAT",
  SUNDAY: "SUN",
};
const WEEKDAYS: DayCode[] = ["MON", "TUE", "WED", "THU", "FRI"];
const defaultRange = (): TimeRange => ({ start: "10:00", end: "17:00" });
const FULL_DAY: TimeRange = { start: "00:00", end: "23:59" };
const isFullDay = (range: TimeRange) =>
  toMinutes(range.start) === 0 && toMinutes(range.end) >= 23 * 60 + 59;
const defaultPerDay = (): Record<DayCode, TimeRange[]> =>
  Object.fromEntries(
    DAYS.map(({ code }) => [code, [defaultRange()]]),
  ) as Record<DayCode, TimeRange[]>;
const defaultTiming = (): Timing => ({
  alwaysOn: true,
  timeMode: "SAME_ALL_DAYS",
  repeat: "WEEKLY",
  timezone: DEFAULT_SLOT_SETTINGS.timezone,
  days: WEEKDAYS,
  sameTimes: [defaultRange()],
  perDay: defaultPerDay(),
});

interface Props {
  legalEntities: LegalEntityHierarchy[];
}

function toMinutes(value: string) {
  const [hour, minute] = value.split(":").map(Number);
  return (hour || 0) * 60 + (minute || 0);
}

function toTimeValue(minutes: number) {
  const clamped = Math.max(0, Math.min(24 * 60, minutes));
  return `${String(Math.floor(clamped / 60) % 24).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}

function formatTime(value: string) {
  if (!value) return "—";
  const [hour, minute] = value.split(":").map(Number);
  const suffix = hour >= 12 ? "PM" : "AM";
  return `${String(hour % 12 || 12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${suffix}`;
}

function formatHourLabel(hour: number) {
  const normalized = hour % 24;
  return `${normalized % 12 || 12} ${normalized >= 12 ? "PM" : "AM"}`;
}

function rangesForDay(timing: Timing, day: DayCode): TimeRange[] {
  if (timing.alwaysOn) return [FULL_DAY];
  return timing.timeMode === "SAME_ALL_DAYS"
    ? timing.sameTimes
    : (timing.perDay[day] ?? []);
}

function activeDaysOf(timing: Timing): DayCode[] {
  if (timing.alwaysOn) return DAYS.map((day) => day.code);
  return timing.repeat === "DAILY"
    ? DAYS.map((day) => day.code)
    : DAYS.filter((day) => timing.days.includes(day.code)).map(
        (day) => day.code,
      );
}

function rangeHours(range: TimeRange) {
  return Math.max(0, toMinutes(range.end) - toMinutes(range.start)) / 60;
}

/** Adds an hour-long window starting after the latest existing one, so it never overlaps. */
function appendWindow(ranges: TimeRange[]): TimeRange[] {
  const latestEnd = ranges.length
    ? Math.max(...ranges.map((range) => toMinutes(range.end)))
    : null;
  const start =
    latestEnd === null
      ? toMinutes(defaultRange().start)
      : Math.min(latestEnd, 23 * 60);
  return [
    ...ranges,
    { start: toTimeValue(start), end: toTimeValue(start + 60) },
  ];
}

function formatRanges(ranges: TimeRange[]) {
  return (
    ranges
      .map((range) => `${formatTime(range.start)} – ${formatTime(range.end)}`)
      .join(", ") || "—"
  );
}

function sortRanges(ranges: TimeRange[]): TimeRange[] {
  return [...ranges].sort((a, b) => toMinutes(a.start) - toMinutes(b.start));
}

/**
 * Validates one day's windows: every window must be a positive span and no two
 * windows may overlap. Touching windows (10:00–11:00, 11:00–12:00) are allowed.
 */
function dayRangeError(ranges: TimeRange[]): string | null {
  for (const range of ranges) {
    if (!range.start || !range.end)
      return "Both start and end time are required.";
    if (toMinutes(range.end) <= toMinutes(range.start))
      return "End time must be after start time.";
  }
  const sorted = sortRanges(ranges);
  for (let index = 1; index < sorted.length; index += 1) {
    if (toMinutes(sorted[index].start) < toMinutes(sorted[index - 1].end)) {
      return `${formatTime(sorted[index].start)} – ${formatTime(sorted[index].end)} overlaps ${formatTime(sorted[index - 1].start)} – ${formatTime(sorted[index - 1].end)}.`;
    }
  }
  return null;
}

/** Per-day validation errors for the days the timing is actually active on. */
function validateTiming(timing: Timing): {
  dayErrors: Partial<Record<DayCode, string>>;
  commonError: string | null;
} {
  if (timing.alwaysOn) return { dayErrors: {}, commonError: null };
  const active = activeDaysOf(timing);
  if (timing.timeMode === "SAME_ALL_DAYS") {
    return { dayErrors: {}, commonError: dayRangeError(timing.sameTimes) };
  }
  const dayErrors: Partial<Record<DayCode, string>> = {};
  for (const day of active) {
    const error = dayRangeError(timing.perDay[day] ?? []);
    if (error) dayErrors[day] = error;
  }
  return { dayErrors, commonError: null };
}

/** Card shell for the groups in this section: tinted header with an icon chip. */
function StepCard({
  icon,
  title,
  action,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-bridge-border bg-white shadow-card">
      <header className="flex items-center justify-between gap-3 rounded-t-xl border-b border-bridge-border/70 bg-bridge-accent-wash px-5 py-4">
        <div className="flex items-center gap-3">
          <IconChip>{icon}</IconChip>
          <h3 className="text-sm font-semibold text-bridge-fg">{title}</h3>
        </div>
        {action}
      </header>
      <div className="px-5 py-5">{children}</div>
    </section>
  );
}

function ChoicePill({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg border px-3.5 py-2 text-xs font-medium transition-colors ${selected ? "border-bridge-accent bg-bridge-accent/5 text-bridge-accent" : "border-bridge-border bg-white text-bridge-muted hover:border-bridge-accent/40"}`}
    >
      <span
        className={`flex h-4 w-4 items-center justify-center rounded-full border ${selected ? "border-bridge-accent bg-bridge-accent text-white" : "border-bridge-border"}`}
      >
        {selected && <Check size={11} strokeWidth={3} />}
      </span>
      {label}
    </button>
  );
}

/** Small on/off switch matching the one used by the other settings sections. */
function Switch({
  checked,
  label,
  disabled,
  onChange,
}: {
  checked: boolean;
  label: string;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-bridge-accent focus:ring-offset-2 ${disabled ? "pointer-events-none cursor-not-allowed opacity-50" : "cursor-pointer"} ${checked ? "bg-bridge-accent" : "border border-bridge-border bg-white"}`}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full shadow ring-0 transition duration-200 ease-in-out ${checked ? "translate-x-4 bg-white" : "translate-x-0 bg-bridge-muted"}`}
      />
    </button>
  );
}

function TimingField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1 text-xs font-medium text-bridge-muted">
        {label}
        <span className="text-red-500">*</span>
        <InfoHint content={`${label} of the bookable window.`} />
      </label>
      <Input
        type="time"
        value={value}
        leading={<Clock3 size={14} />}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function DayCard({
  day,
  selected,
  ranges,
  editable,
  editingIndex,
  disabled,
  error,
  onToggle,
  onEdit,
  onRemove,
  onAdd,
}: {
  day: { code: DayCode; label: string };
  selected: boolean;
  ranges: TimeRange[];
  editable: boolean;
  editingIndex: number | null;
  disabled: boolean;
  error?: string;
  onToggle: () => void;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
}) {
  return (
    <div
      className={`flex min-w-0 flex-col rounded-xl border p-3 transition-colors ${error ? "border-bridge-danger bg-bridge-danger/5" : selected ? "border-bridge-accent/40 bg-bridge-accent/5" : "border-bridge-border bg-white"}`}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={onToggle}
        aria-pressed={selected}
        className="flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed"
      >
        <span
          className={`text-sm font-semibold ${selected ? "text-bridge-fg" : "text-bridge-muted"}`}
        >
          {day.label}
        </span>
        <span
          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${selected ? "border-bridge-accent bg-bridge-accent text-white" : "border-bridge-border bg-white"}`}
        >
          {selected && <Check size={11} strokeWidth={3} />}
        </span>
      </button>

      {!selected && (
        <p className="mt-2 text-[11px] text-bridge-subtle">Not selected</p>
      )}

      {selected && (
        <div className="mt-2 space-y-1.5">
          {ranges.map((range, index) => (
            <div
              key={index}
              className={`flex items-center gap-1 rounded-md border px-1.5 py-1 text-[10px] tabular-nums ${editingIndex === index ? "border-bridge-accent bg-white text-bridge-accent" : "border-bridge-border bg-white text-bridge-fg"}`}
            >
              <button
                type="button"
                onClick={() => onEdit(index)}
                disabled={!editable}
                className="min-w-0 flex-1 truncate text-left disabled:cursor-default"
                title={editable ? "Edit this window" : undefined}
              >
                {formatTime(range.start)} – {formatTime(range.end)}
              </button>
              {editable && (
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  aria-label={`Remove ${day.label} window ${index + 1}`}
                  className="shrink-0 text-bridge-subtle hover:text-bridge-danger"
                >
                  <X size={11} />
                </button>
              )}
            </div>
          ))}
          {editable && (
            <button
              type="button"
              onClick={onAdd}
              className="flex items-center gap-1 pt-0.5 text-[11px] font-medium text-bridge-accent"
            >
              <Plus size={11} /> Add time
            </button>
          )}
          {error && (
            <p className="text-[10px] leading-tight text-bridge-danger">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Week grid showing the bookable windows the current settings produce. */
function LivePreview({ timing }: { timing: Timing }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const activeDays = activeDaysOf(timing);

  const weekStart = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    // getDay() is Sunday-first; shift so the grid always starts on Monday.
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7) + weekOffset * 7);
    return date;
  }, [weekOffset]);

  const dates = DAYS.map((_, index) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + index);
    return date;
  });
  const weekEnd = dates[6];
  const rangeLabel = `${weekStart.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${weekEnd.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;

  const allRanges = activeDays.flatMap((day) => rangesForDay(timing, day));
  const earliest = allRanges.length
    ? Math.min(...allRanges.map((range) => toMinutes(range.start)))
    : 9 * 60;
  const latest = allRanges.length
    ? Math.max(...allRanges.map((range) => toMinutes(range.end)))
    : 18 * 60;
  const gridStart = Math.max(0, Math.floor(earliest / 60) - 1);
  const gridEnd = Math.min(24, Math.ceil(latest / 60) + 1);
  const hours = Array.from(
    { length: Math.max(1, gridEnd - gridStart) },
    (_, index) => gridStart + index,
  );
  const ROW = 44;

  const weeklyHours = activeDays.reduce(
    (total, day) =>
      total +
      rangesForDay(timing, day).reduce(
        (sum, range) => sum + rangeHours(range),
        0,
      ),
    0,
  );
  const perDayHours = activeDays.map((day) =>
    rangesForDay(timing, day).reduce(
      (sum, range) => sum + rangeHours(range),
      0,
    ),
  );
  const uniformDay =
    perDayHours.length > 0 &&
    perDayHours.every((value) => value === perDayHours[0]);
  const dailyWindow = timing.alwaysOn
    ? "Round the clock"
    : timing.timeMode === "SAME_ALL_DAYS"
      ? formatRanges(timing.sameTimes)
      : "Varies by day";
  const daysLabel =
    activeDays.length === 0
      ? "No days selected"
      : activeDays.length === 7
        ? "Every day"
        : activeDays
            .map((day) => DAYS.find((item) => item.code === day)?.label)
            .join(", ");

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-bridge-border bg-white p-5 shadow-card">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-bridge-accent" />
          <h3 className="text-sm font-semibold text-bridge-fg">Live Preview</h3>
        </div>
        <p className="mt-1 text-xs text-bridge-muted">
          This is how your bookable slots will look based on the current
          settings.
          {timing.timezone ? ` Times shown in ${timing.timezone}.` : ""}
        </p>

        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Previous week"
              onClick={() => setWeekOffset((current) => current - 1)}
              className="rounded-md border border-bridge-border p-1 text-bridge-muted hover:text-bridge-accent"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs font-medium text-bridge-fg">
              {rangeLabel}
            </span>
            <button
              type="button"
              aria-label="Next week"
              onClick={() => setWeekOffset((current) => current + 1)}
              className="rounded-md border border-bridge-border p-1 text-bridge-muted hover:text-bridge-accent"
            >
              <ChevronRight size={14} />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setWeekOffset(0)}
            className="rounded-md border border-bridge-border px-2.5 py-1 text-[11px] font-medium text-bridge-muted hover:text-bridge-accent"
          >
            This week
          </button>
        </div>

        <div className="mt-3 overflow-x-auto">
          <div className="min-w-[520px]">
            <div className="grid grid-cols-[44px_repeat(7,minmax(0,1fr))] border-b border-bridge-border/70 pb-2 text-center">
              <span />
              {DAYS.map((day, index) => (
                <div key={day.code} className="text-[11px]">
                  <div className="font-medium text-bridge-fg">{day.label}</div>
                  <div className="text-bridge-subtle">
                    {dates[index].getDate()}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-[44px_repeat(7,minmax(0,1fr))]">
              <div>
                {hours.map((hour) => (
                  <div
                    key={hour}
                    style={{ height: ROW }}
                    className="pr-2 text-right text-[10px] text-bridge-subtle"
                  >
                    {formatHourLabel(hour)}
                  </div>
                ))}
              </div>

              {DAYS.map((day) => {
                const selected = activeDays.includes(day.code);
                return (
                  <div
                    key={day.code}
                    className="relative border-l border-bridge-border/50"
                    style={{ height: hours.length * ROW }}
                  >
                    {hours.map((hour) => (
                      <div
                        key={hour}
                        style={{ height: ROW }}
                        className="border-b border-bridge-border/40"
                      />
                    ))}
                    {selected &&
                      rangesForDay(timing, day.code).map((range, index) => {
                        const top =
                          ((toMinutes(range.start) - gridStart * 60) / 60) *
                          ROW;
                        const height = Math.max(18, rangeHours(range) * ROW);
                        return (
                          <div
                            key={index}
                            style={{ top, height }}
                            className="absolute inset-x-0.5 overflow-hidden rounded-md border border-bridge-accent/40 bg-bridge-accent/10 px-1 py-0.5 text-[9px] leading-tight text-bridge-accent"
                          >
                            <div>{formatTime(range.start)}</div>
                            <div>– {formatTime(range.end)}</div>
                          </div>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          {
            icon: <CalendarDays size={16} />,
            value: String(activeDays.length),
            label: "Active days",
            hint: daysLabel,
          },
          {
            icon: <Clock3 size={16} />,
            value:
              uniformDay && perDayHours.length
                ? `${Number(perDayHours[0].toFixed(2))}h`
                : "—",
            label: "Daily window",
            hint: dailyWindow,
          },
          {
            icon: <BarChart3 size={16} />,
            value: `${Number(weeklyHours.toFixed(2))}h`,
            label: "Total weekly",
            hint: "Bookable hours",
          },
        ].map((tile) => (
          <div
            key={tile.label}
            className="flex items-center gap-3 rounded-xl border border-bridge-border bg-white p-4 shadow-card"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-bridge-accent/10 text-bridge-accent">
              {tile.icon}
            </span>
            <div className="min-w-0">
              <div className="text-lg font-semibold tabular-nums text-bridge-fg">
                {tile.value}
              </div>
              <div className="text-[11px] font-medium text-bridge-muted">
                {tile.label}
              </div>
              <div
                className="truncate text-[11px] text-bridge-subtle"
                title={tile.hint}
              >
                {tile.hint}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function timingFromPayload(payload: LiveHoursPayload): Timing {
  const scheduleByDay = new Map(
    payload.schedule.map((item) => [UI_DAYS[item.day], item]),
  );
  // Same-time mode reads commonSchedule; fall back to the first day that carries windows.
  const commonWindows: LiveHoursTiming[] = payload.commonSchedule?.length
    ? payload.commonSchedule
    : (payload.schedule.find((item) => item.timing?.length)?.timing ?? []);
  const sameTimes: TimeRange[] = commonWindows.length
    ? sortRanges(
        commonWindows.map((window) => ({
          start: window.startTime,
          end: window.endTime,
        })),
      )
    : [defaultRange()];

  const everyDayFull =
    payload.schedule.length > 0 &&
    payload.schedule.every((item) => item.enabled) &&
    payload.commonSchedule?.length === 1 &&
    isFullDay({
      start: payload.commonSchedule[0].startTime,
      end: payload.commonSchedule[0].endTime,
    });

  return {
    // Older records predate the flag, so a midnight-to-midnight week reads as 24×7 too.
    alwaysOn: payload.alwaysOn ?? everyDayFull,
    repeat: payload.repeatMode,
    timezone: payload.timezone || DEFAULT_SLOT_SETTINGS.timezone,
    timeMode:
      payload.timeMode === "DIFFERENT_PER_DAY"
        ? "CUSTOM_PER_DAY"
        : "SAME_ALL_DAYS",
    days: payload.schedule
      .filter((item) => item.enabled)
      .map((item) => UI_DAYS[item.day]),
    sameTimes,
    perDay: Object.fromEntries(
      DAYS.map(({ code }) => {
        const item = scheduleByDay.get(code);
        const windows = item?.timing?.length
          ? item.timing.map((window) => ({
              start: window.startTime,
              end: window.endTime,
            }))
          : sameTimes.map((range) => ({ ...range }));
        return [code, sortRanges(windows)];
      }),
    ) as Record<DayCode, TimeRange[]>,
  };
}

function payloadFromTiming(
  timing: Timing,
  legalEntityId: string,
  branchId: string | null,
): LiveHoursPayload {
  const enabledDays = new Set(
    timing.alwaysOn || timing.repeat === "DAILY"
      ? DAYS.map((day) => day.code)
      : timing.days,
  );
  const scope = branchId ? "BRANCH" : "LEGAL_ENTITY";
  if (timing.alwaysOn) {
    // Send a full week of full days as well as the flag, so consumers that
    // only read the schedule still see round-the-clock availability.
    return {
      scope,
      legalEntityId,
      branchId,
      alwaysOn: true,
      repeatMode: "DAILY",
      timezone: timing.timezone,
      timeMode: "SAME_ALL_DAYS",
      commonSchedule: [{ startTime: FULL_DAY.start, endTime: FULL_DAY.end }],
      schedule: DAYS.map(({ code }) => ({
        day: API_DAYS[code],
        enabled: true,
        timing: null,
      })),
    };
  }
  return {
    scope,
    legalEntityId,
    branchId,
    alwaysOn: false,
    repeatMode: timing.repeat,
    timezone: timing.timezone,
    timeMode:
      timing.timeMode === "CUSTOM_PER_DAY"
        ? "DIFFERENT_PER_DAY"
        : "SAME_ALL_DAYS",
    commonSchedule:
      timing.timeMode === "SAME_ALL_DAYS"
        ? sortRanges(timing.sameTimes).map((range) => ({
            startTime: range.start,
            endTime: range.end,
          }))
        : null,
    schedule: DAYS.map(({ code }) => {
      const enabled = enabledDays.has(code);
      const custom = timing.timeMode === "CUSTOM_PER_DAY" && enabled;
      const windows = custom ? sortRanges(timing.perDay[code] ?? []) : [];
      return {
        day: API_DAYS[code],
        enabled,
        timing: custom
          ? windows.map((range) => ({
              startTime: range.start,
              endTime: range.end,
            }))
          : null,
      };
    }),
  };
}

/** Scope controls, the timing editor and a live preview for the live-hours settings group. */
export function LiveHoursSection({ legalEntities }: Props) {
  const { legalEntityId, branchId, setLegalEntity, setBranch } =
    useSettingsStore();
  const [timing, setTiming] = useState<Timing>(defaultTiming);
  const [savedTiming, setSavedTiming] = useState<SavedTiming | null>(null);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!legalEntityId) {
      setTiming(defaultTiming());
      setSavedTiming(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void liveHoursService
      .get({ legalEntityId, branchId })
      .then((payload) => {
        if (cancelled) return;
        const next = timingFromPayload(payload);
        setTiming(next);
        setSavedTiming({
          ...next,
          legalEntityId: payload.legalEntityId,
          branchId: payload.branchId,
        });
      })
      .catch(() => {
        if (!cancelled) {
          const next = defaultTiming();
          setTiming(next);
          setSavedTiming(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      setEditing(null);
    };
  }, [legalEntityId, branchId]);

  const allLegalEntities = useMemo(
    () => flattenEntities(legalEntities),
    [legalEntities],
  );
  const selectedEntity = legalEntityId
    ? findEntityById(legalEntities, legalEntityId)
    : undefined;
  const legalEntityOptions = allLegalEntities.map((entity) => ({
    value: entity.id,
    label: entity.name,
  }));
  const branchOptions = [
    { value: "", label: "All Branches" },
    ...(selectedEntity?.branches.map((branch) => ({
      value: branch.id,
      label: branch.name,
    })) ?? []),
  ];
  const activeDays = activeDaysOf(timing);
  const custom = timing.timeMode === "CUSTOM_PER_DAY";
  const editingRange = editing
    ? timing.perDay[editing.day]?.[editing.index]
    : undefined;
  const { dayErrors, commonError } = useMemo(
    () => validateTiming(timing),
    [timing],
  );
  const timezoneError = timing.timezone ? null : "Select a timezone.";
  const hasErrors =
    Boolean(commonError) ||
    Boolean(timezoneError) ||
    Object.keys(dayErrors).length > 0;
  // Compare through the payload shape so fields the current mode ignores
  // (per-day windows in same-time mode, days while 24×7 is on) never read as changes.
  // With nothing saved yet for this scope, the defaults are the baseline.
  const isDirty = useMemo(() => {
    if (!legalEntityId) return false;
    const asPayload = (value: Timing) =>
      JSON.stringify(payloadFromTiming(value, legalEntityId, branchId));
    return asPayload(timing) !== asPayload(savedTiming ?? defaultTiming());
  }, [timing, savedTiming, legalEntityId, branchId]);

  const unsavedGuard = useUnsavedChangesGuard(isDirty);

  function resetTiming() {
    if (!savedTiming) {
      setTiming(defaultTiming());
      setEditing(null);
      return;
    }
    setTiming({
      alwaysOn: savedTiming.alwaysOn,
      timeMode: savedTiming.timeMode,
      repeat: savedTiming.repeat,
      timezone: savedTiming.timezone,
      days: [...savedTiming.days],
      sameTimes: savedTiming.sameTimes.map((range) => ({ ...range })),
      perDay: Object.fromEntries(
        Object.entries(savedTiming.perDay).map(([day, ranges]) => [
          day,
          ranges.map((range) => ({ ...range })),
        ]),
      ) as Timing["perDay"],
    });
    setEditing(null);
  }

  const setDayRanges = (
    day: DayCode,
    next: (ranges: TimeRange[]) => TimeRange[],
  ) =>
    setTiming((current) => ({
      ...current,
      perDay: { ...current.perDay, [day]: next(current.perDay[day] ?? []) },
    }));

  const toggleDay = (day: DayCode) =>
    setTiming((current) => {
      const selected = current.days.includes(day);
      const days = selected
        ? current.days.filter((code) => code !== day)
        : [...current.days, day];
      // Re-selecting a day that was emptied out needs a window to show.
      const perDay =
        !selected && (current.perDay[day]?.length ?? 0) === 0
          ? { ...current.perDay, [day]: [defaultRange()] }
          : current.perDay;
      return { ...current, days, perDay };
    });

  const addWindow = (day: DayCode) => setDayRanges(day, appendWindow);

  const addCommonWindow = () =>
    setTiming((current) => ({
      ...current,
      sameTimes: appendWindow(current.sameTimes),
    }));

  const removeCommonWindow = (index: number) =>
    setTiming((current) => ({
      ...current,
      sameTimes: current.sameTimes.filter((_, position) => position !== index),
    }));

  const updateCommonWindow = (
    index: number,
    field: keyof TimeRange,
    value: string,
  ) => setCommonWindow(index, (range) => ({ ...range, [field]: value }));

  /** Both ends at once — what the slider hands back after a drag. */
  const setCommonWindow = (
    index: number,
    next: (range: TimeRange) => TimeRange,
  ) =>
    setTiming((current) => ({
      ...current,
      sameTimes: current.sameTimes.map((range, position) =>
        position === index ? next(range) : range,
      ),
    }));

  const removeWindow = (day: DayCode, index: number) => {
    setDayRanges(day, (ranges) =>
      ranges.filter((_, position) => position !== index),
    );
    setEditing((current) => (current && current.day === day ? null : current));
    // A day with no windows left is no longer active.
    setTiming((current) =>
      (current.perDay[day]?.length ?? 0) === 0
        ? { ...current, days: current.days.filter((code) => code !== day) }
        : current,
    );
  };

  const updateWindow = (field: keyof TimeRange, value: string) =>
    setEditingWindow((range) => ({ ...range, [field]: value }));

  const setEditingWindow = (next: (range: TimeRange) => TimeRange) => {
    if (!editing) return;
    setDayRanges(editing.day, (ranges) =>
      ranges.map((range, index) =>
        index === editing.index ? next(range) : range,
      ),
    );
  };

  const saveTiming = async () => {
    if (!legalEntityId) return;
    if (hasErrors) {
      toast.error(
        timezoneError ??
          commonError ??
          "Fix the overlapping timings before saving.",
      );
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromTiming(timing, legalEntityId, branchId);
      const saved = await liveHoursService.save(payload);
      const next = timingFromPayload(saved);
      setTiming(next);
      setEditing(null);
      setSavedTiming({
        ...next,
        legalEntityId: saved.legalEntityId,
        branchId: saved.branchId,
      });
      toast.success("Live hours saved.");
    } catch {
      toast.error("Failed to save live hours.");
    } finally {
      setSaving(false);
    }
  };

  const savedDays = savedTiming ? activeDaysOf(savedTiming) : [];

  return (
    <div className="relative">
      {/* Scope selection, pinned to the top of the page scroll like Viewing
          Settings so "what am I editing" stays on screen for the whole edit. */}
      <div className={SCOPE_BAR_CLASS}>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-bridge-muted">
              <Building2 size={13} className="text-bridge-accent" /> Legal
              Entity
            </label>
            <MenuSelect
              value={legalEntityId ?? ""}
              onChange={(id) => {
                unsavedGuard.confirm(() => {
                  const entity = findEntityById(legalEntities, id);
                  if (entity) setLegalEntity(entity.id, entity.name);
                });
              }}
              options={legalEntityOptions}
              placeholder="Select Legal Entity"
              enableSearch
            />
          </div>
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-bridge-muted">
              <GitBranch size={13} className="text-bridge-accent" /> Branch
            </label>
            <MenuSelect
              value={branchId ?? ""}
              onChange={(id) => {
                unsavedGuard.confirm(() => {
                  if (!id) {
                    setBranch(null, null);
                    return;
                  }
                  const branch = branchOptions.find(
                    (option) => option.value === id,
                  );
                  if (branch) setBranch(branch.value, branch.label);
                });
              }}
              options={branchOptions}
              placeholder="All Branches"
              disabled={!legalEntityId}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-4 px-5 py-5 xl:grid-cols-[minmax(0,1fr)_minmax(380px,460px)]">
        <div className="space-y-4">
          <StepCard icon={<CalendarDays size={15} />} title="Timing Rules">
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-4 rounded-xl border border-bridge-border bg-bridge-bg/40 px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1 text-sm font-medium text-bridge-fg">
                    Available 24×7
                    <InfoHint content="On means Live Hours runs round the clock on every day, and the day and window rules below no longer apply." />
                  </div>
                  <p className="mt-0.5 text-[11px] text-bridge-muted">
                    Keep the Live Hours service open all day, every day. Turn it
                    off to pick specific days and windows.
                  </p>
                </div>
                <Switch
                  checked={timing.alwaysOn}
                  label="Available 24×7"
                  disabled={!legalEntityId || loading}
                  onChange={(next) => {
                    setEditing(null);
                    setTiming((current) => ({ ...current, alwaysOn: next }));
                  }}
                />
              </div>

              {timing.alwaysOn && (
                <p className="rounded-lg bg-bridge-accent/5 px-3 py-2 text-[11px] text-bridge-muted">
                  Every day is fully bookable while 24×7 is on. Saving now
                  replaces any day-by-day windows for this scope.
                </p>
              )}

              {/* 24×7 leaves the rules below in place but inert. */}
              <div
                className={`space-y-5 ${timing.alwaysOn ? "pointer-events-none opacity-50" : ""}`}
                aria-disabled={timing.alwaysOn}
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <div className="mb-2 flex items-center gap-1 text-xs font-medium text-bridge-muted">
                      Repeat{" "}
                      <InfoHint content="Weekly repeats on the days you pick; Daily activates every day." />
                    </div>
                    <div role="radiogroup" className="flex gap-2">
                      {(["WEEKLY", "DAILY"] as RepeatMode[]).map((mode) => (
                        <ChoicePill
                          key={mode}
                          selected={timing.repeat === mode}
                          label={mode === "WEEKLY" ? "Weekly" : "Daily"}
                          onClick={() =>
                            setTiming((current) => ({
                              ...current,
                              repeat: mode,
                              days:
                                mode === "DAILY"
                                  ? DAYS.map((day) => day.code)
                                  : current.days.length
                                    ? current.days
                                    : WEEKDAYS,
                            }))
                          }
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 flex items-center gap-1 text-xs font-medium text-bridge-muted">
                      Time pattern{" "}
                      <InfoHint content="One window for every active day, or separate windows per day." />
                    </div>
                    <div role="radiogroup" className="flex flex-wrap gap-2">
                      {(["SAME_ALL_DAYS", "CUSTOM_PER_DAY"] as TimeMode[]).map(
                        (mode) => (
                          <ChoicePill
                            key={mode}
                            selected={timing.timeMode === mode}
                            label={
                              mode === "SAME_ALL_DAYS"
                                ? "Same time each day"
                                : "Different times per day"
                            }
                            onClick={() => {
                              setEditing(null);
                              setTiming((current) => ({
                                ...current,
                                timeMode: mode,
                              }));
                            }}
                          />
                        ),
                      )}
                    </div>
                  </div>
                </div>

                <div className="md:max-w-sm">
                  <label className="mb-1.5 flex items-center gap-1 text-xs font-medium text-bridge-muted">
                    Timezone<span className="text-red-500">*</span>
                    <InfoHint content="Timezone the window times and generated slots are interpreted in." />
                  </label>
                  <MenuSelect
                    hideLabel
                    value={timing.timezone}
                    onChange={(zone) =>
                      setTiming((current) => ({ ...current, timezone: zone }))
                    }
                    options={TIMEZONE_OPTIONS}
                    searchable
                    placeholder="Select timezone"
                  />
                  {timezoneError && (
                    <p className="mt-1 text-[11px] text-bridge-danger">
                      {timezoneError}
                    </p>
                  )}
                </div>

                {!custom && (
                  <div className="space-y-3">
                    {timing.sameTimes.map((range, index) => (
                      <div key={index} className="flex items-end gap-3">
                        <div className="flex-1 space-y-2">
                          {/* Same slider Viewing settings uses — it and the two
                          fields below edit the same values, both directions. */}
                          <TimeWindowSlider
                            start={range.start}
                            end={range.end}
                            onChange={(next) =>
                              setCommonWindow(index, () => next)
                            }
                          />
                          <div className="grid gap-4 md:grid-cols-2">
                            <TimingField
                              label="Window start"
                              value={range.start}
                              onChange={(value) =>
                                updateCommonWindow(index, "start", value)
                              }
                            />
                            <TimingField
                              label="Window end"
                              value={range.end}
                              onChange={(value) =>
                                updateCommonWindow(index, "end", value)
                              }
                            />
                          </div>
                        </div>
                        {timing.sameTimes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeCommonWindow(index)}
                            aria-label={`Remove window ${index + 1}`}
                            className="mb-1.5 rounded-md border border-bridge-border p-2 text-bridge-subtle hover:border-bridge-danger hover:text-bridge-danger"
                          >
                            <X size={13} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addCommonWindow}
                      className="flex items-center gap-1 text-xs font-medium text-bridge-accent"
                    >
                      <Plus size={12} /> Add time
                    </button>
                    <p className="text-[11px] text-bridge-subtle">
                      These windows apply to every active day.
                    </p>
                  </div>
                )}

                <div>
                  <div className="mb-2 flex items-center gap-1 text-xs font-medium text-bridge-muted">
                    Days{" "}
                    <InfoHint content="Select the days this timing repeats on." />
                  </div>
                  <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-7">
                    {DAYS.map((day) => (
                      <DayCard
                        key={day.code}
                        day={day}
                        selected={activeDays.includes(day.code)}
                        ranges={rangesForDay(timing, day.code)}
                        editable={custom}
                        editingIndex={
                          editing?.day === day.code ? editing.index : null
                        }
                        disabled={timing.repeat === "DAILY"}
                        error={custom ? dayErrors[day.code] : undefined}
                        onToggle={() => toggleDay(day.code)}
                        onEdit={(index) =>
                          setEditing((current) =>
                            current?.day === day.code && current.index === index
                              ? null
                              : { day: day.code, index },
                          )
                        }
                        onRemove={(index) => removeWindow(day.code, index)}
                        onAdd={() => {
                          addWindow(day.code);
                          setEditing({
                            day: day.code,
                            index: rangesForDay(timing, day.code).length,
                          });
                        }}
                      />
                    ))}
                  </div>

                  {custom && editingRange && editing && (
                    <div className="mt-3 rounded-xl border border-bridge-accent/40 bg-bridge-accent/5 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-xs font-semibold text-bridge-fg">
                          Editing{" "}
                          {
                            DAYS.find((item) => item.code === editing.day)
                              ?.label
                          }{" "}
                          · window {editing.index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => setEditing(null)}
                          className="text-xs font-medium text-bridge-accent"
                        >
                          Done
                        </button>
                      </div>
                      <div className="space-y-2">
                        <TimeWindowSlider
                          start={editingRange.start}
                          end={editingRange.end}
                          onChange={(next) => setEditingWindow(() => next)}
                        />
                        <div className="grid gap-3 sm:grid-cols-2">
                          <TimingField
                            label="Window start"
                            value={editingRange.start}
                            onChange={(value) => updateWindow("start", value)}
                          />
                          <TimingField
                            label="Window end"
                            value={editingRange.end}
                            onChange={(value) => updateWindow("end", value)}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-2 flex items-center gap-3">
                    <p className="text-[11px] text-bridge-subtle">
                      {timing.repeat === "DAILY"
                        ? "Every day is active."
                        : custom
                          ? "Click a window to edit it, or add another."
                          : "Select the days this timing repeats on."}
                    </p>
                    {timing.repeat === "WEEKLY" && (
                      <button
                        type="button"
                        onClick={() =>
                          setTiming((current) => ({
                            ...current,
                            days: WEEKDAYS,
                          }))
                        }
                        className="text-xs font-medium text-bridge-accent"
                      >
                        Select Mon–Fri
                      </button>
                    )}
                  </div>

                  {(commonError || Object.keys(dayErrors).length > 0) && (
                    <div className="mt-2 space-y-1 rounded-lg bg-bridge-danger/10 px-3 py-2 text-[11px] text-bridge-danger">
                      {commonError && <p>{commonError}</p>}
                      {DAYS.filter((day) => dayErrors[day.code]).map((day) => (
                        <p key={day.code}>
                          {day.label}: {dayErrors[day.code]}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </StepCard>

          <section className="rounded-xl border border-bridge-border bg-white shadow-card">
            <header className="flex items-center gap-3 rounded-t-xl border-b border-bridge-border/70 bg-bridge-accent-wash px-5 py-4">
              <IconChip>
                <Clock3 size={15} />
              </IconChip>
              <h3 className="text-sm font-semibold text-bridge-fg">
                Saved Timings
              </h3>
            </header>
            <div className="px-5 py-5">
              {savedTiming ? (
                <div className="rounded-lg border border-bridge-border bg-bridge-bg/40 p-4 text-sm text-bridge-fg">
                  <div className="font-medium">
                    {savedTiming.alwaysOn
                      ? "Every day · 24×7"
                      : savedTiming.repeat === "DAILY"
                        ? "Every day"
                        : savedDays
                            .map(
                              (day) =>
                                DAYS.find((item) => item.code === day)?.label,
                            )
                            .join(", ") || "No days selected"}
                  </div>
                  {savedTiming.alwaysOn ? (
                    <p className="mt-1 text-xs text-bridge-muted">
                      Open round the clock
                    </p>
                  ) : savedTiming.timeMode === "SAME_ALL_DAYS" ? (
                    <p className="mt-1 text-xs text-bridge-muted">
                      {formatRanges(savedTiming.sameTimes)} · Same time each day
                    </p>
                  ) : (
                    <div className="mt-2 grid gap-1 text-xs text-bridge-muted md:grid-cols-2">
                      {savedDays.map((day) => (
                        <div key={day}>
                          {DAYS.find((item) => item.code === day)?.label}:{" "}
                          {formatRanges(savedTiming.perDay[day] ?? [])}
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-[11px] text-bridge-subtle">
                    Timezone: {savedTiming.timezone}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-bridge-muted">
                  No timings saved for this selection yet.
                </p>
              )}
            </div>
          </section>
        </div>

        <LivePreview timing={timing} />
      </div>

      {/* Footer bar — docked to the bottom of the page scroll so Save is always
          in reach, matching the other settings sections. */}
      <div className={actionBarClass(isDirty)}>
        <SaveStatus dirty={isDirty} />
        <span className="ml-auto" />
        <Button
          variant="outline"
          onClick={resetTiming}
          disabled={!isDirty || saving || loading}
          title="Discard changes and restore the last saved values"
        >
          Reset
        </Button>
        <Button
          variant="primary"
          onClick={() => void saveTiming()}
          loading={saving}
          disabled={!legalEntityId || loading || hasErrors || !isDirty}
        >
          Save timings
        </Button>
      </div>

      <Dialog
        open={unsavedGuard.open}
        title="Discard unsaved changes?"
        subtitle="Your edits to these live hours settings have not been saved yet."
        submitLabel="Yes, discard"
        cancelLabel="No, stay here"
        onClose={unsavedGuard.cancel}
        onSubmit={unsavedGuard.proceed}
      >
        <p className="text-sm text-bridge-muted">
          Leaving this scope will lose the changes you have made. Save them
          first if you want to keep them.
        </p>
      </Dialog>
    </div>
  );
}
